import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import axios from 'axios';
import { useToast } from './ui/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Canonical deal status catalog with UI styling
export const STATUSES = [
  { value: 'Active', label: 'Active', dot: 'bg-blue-500', pill: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'Fully Paid', label: 'Fully Paid', dot: 'bg-green-500', pill: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'In Collections', label: 'In Collections', dot: 'bg-orange-500', pill: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'Default', label: 'Default', dot: 'bg-red-500', pill: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'Restructured', label: 'Restructured', dot: 'bg-purple-500', pill: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'Paused', label: 'Paused', dot: 'bg-gray-400', pill: 'bg-gray-100 text-gray-700 border-gray-200' },
];

export const normalizeStatus = (raw) => {
  if (!raw || raw === 'Current') return 'Active';
  return raw;
};

export const getStatusMeta = (raw) => {
  const v = normalizeStatus(raw);
  return STATUSES.find(s => s.value === v) || STATUSES[0];
};

/**
 * Dropdown that changes a deal's paid_status via PUT /api/deals/:id/status
 * Triggers audit log + notifications on backend.
 */
const DealStatusDropdown = ({ dealId, currentStatus, onChanged, size = 'md', disabled = false }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [reason, setReason] = useState('');
  const ref = useRef(null);

  const active = getStatusMeta(currentStatus);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const commitStatus = async (newStatus, reasonText = '') => {
    setSaving(true);
    try {
      const token = localStorage.getItem('mcaToken');
      const res = await axios.put(
        `${API_BASE}/deals/${dealId}/status`,
        { status: newStatus, reason: reasonText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.error) {
        toast({ title: 'Error', description: res.data.error, variant: 'destructive' });
      } else {
        toast({ title: 'Status updated', description: `Changed to ${newStatus}` });
        if (onChanged) onChanged(newStatus);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to change status', variant: 'destructive' });
    } finally {
      setSaving(false);
      setPendingStatus(null);
      setReason('');
      setOpen(false);
    }
  };

  const handleSelect = (s) => {
    if (s.value === active.value) { setOpen(false); return; }
    // Serious statuses require confirmation + optional reason
    if (s.value === 'Default' || s.value === 'In Collections') {
      setPendingStatus(s);
      return;
    }
    commitStatus(s.value);
  };

  const sizeCls = size === 'sm'
    ? 'px-2 py-1 text-xs'
    : size === 'lg'
      ? 'px-4 py-2 text-sm'
      : 'px-3 py-1.5 text-xs';

  return (
    <div className="relative inline-block" ref={ref} data-testid={`status-dropdown-${dealId}`}>
      <button
        type="button"
        disabled={disabled || saving}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={`inline-flex items-center gap-2 rounded-full border ${active.pill} ${sizeCls} font-medium hover:brightness-95 disabled:opacity-50`}
        data-testid={`status-current-${dealId}`}
      >
        <span className={`w-2 h-2 rounded-full ${active.dot}`}></span>
        <span>{active.label}</span>
        <ChevronDown size={14} />
      </button>

      {open && (
        <div
          className="absolute z-30 mt-1 min-w-[180px] rounded-md border border-gray-200 bg-white shadow-lg"
          onClick={(e) => e.stopPropagation()}
          data-testid={`status-menu-${dealId}`}
        >
          {STATUSES.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => handleSelect(s)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
              data-testid={`status-option-${s.value.replace(/\s+/g, '-').toLowerCase()}-${dealId}`}
            >
              <span className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
                {s.label}
              </span>
              {s.value === active.value && <Check size={14} className="text-green-600" />}
            </button>
          ))}
        </div>
      )}

      {/* Confirmation modal for serious statuses */}
      {pendingStatus && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => e.stopPropagation()}
          data-testid={`status-confirm-${pendingStatus.value.replace(/\s+/g, '-').toLowerCase()}`}
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-1 text-lg font-semibold text-gray-900">Confirm status change</h3>
            <p className="mb-4 text-sm text-gray-600">
              Change status to <strong className={pendingStatus.value === 'Default' ? 'text-red-600' : 'text-orange-600'}>{pendingStatus.label}</strong>?
              Deal owner and admins will be notified and this change will be audit-logged.
            </p>
            <label className="mb-4 block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Reason (optional)</span>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Missed 5 consecutive payments"
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-orange-500 focus:outline-none"
                data-testid="status-reason-input"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setPendingStatus(null); setReason(''); }}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                data-testid="status-confirm-cancel"
              >Cancel</button>
              <button
                type="button"
                disabled={saving}
                onClick={() => commitStatus(pendingStatus.value, reason)}
                className={`rounded px-3 py-1.5 text-sm text-white disabled:opacity-50 ${pendingStatus.value === 'Default' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                data-testid="status-confirm-apply"
              >{saving ? 'Saving…' : `Set ${pendingStatus.label}`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealStatusDropdown;
